import React, { FormEvent, PointerEvent, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import styled, { css } from 'styled-components';
import { Voice, VoiceValues } from '../../../common/src/model.ts';
import { getMeasureNumberForDisplay, MeasureVisit } from '../../../common/src/syp_file.ts';
import { formatMinutesSeconds } from '../../../common/src/time_string.ts';
import OutlinedHeartIcon from '../assets/heart-outline.svg?react';
import HeartIcon from '../assets/heart.svg?react';
import Repeat1Icon from '../assets/repeat-1.svg?react';
import RepeatIcon from '../assets/repeat.svg?react';
import { defaultVerseCount, LocalStorageKey, RepeatMode } from '../data/client_local_storage.ts';
import { FavoritesContext } from '../data/use_favorites.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { isCustomSong } from '../util/path.ts';
import { CurrentSongContext, getMeasureFromPercent, roundMeasurePercent } from './current_song.tsx';
import { mixerLandscapeContainerQuery } from './player_container_queries.ts';
import {
  locationSliderUpdateIntervalMs,
  SliderButtonAndRangeWrapper,
  SliderButtonWrapper,
  SliderLabel,
  swipeDeltas,
} from './player_util.tsx';
import { PracticeVideoContext } from './practice_video/practice_video_context.tsx';
import { PracticeVideoMode } from './practice_video/practice_video_mode.tsx';
import { SourceSelector } from './practice_video/source_selector.tsx';
import { SequencerContext } from './sequencer.tsx';
import { TempoSlider } from './tempo_sliders/tempo_slider.tsx';
import { VerseBar } from './verse_bar.tsx';
import {
  findVisitIndexToSeek,
  getVerseCount,
  getVerseStartVisitIndex,
  verseIndexFromNumber,
  verseNumberFromIndex,
  VisitPass,
} from './verse_navigation.ts';
import { VoiceButton } from './voice_button.tsx';
import { VoiceSlider } from './voice_slider.tsx';

export function MixerControls({ showPDF }: { showPDF: () => void }) {
  const {
    duration,
    hymn,
    getCurrentPercent,
    currentVerse: currentVerseRaw,
    sequencer,
    songContainsRepeats,
    sypFile,
    sypFileParsed,
    readyToPlay,
    vocalsEnabled,
    videoEnabled,
    voices,
    liveControls,
    setLiveVoiceValue,
  } = use(SequencerContext);
  const { isVideoMode } = use(PracticeVideoContext);
  // a recording (vocals or a practice video) is placed by time; the piano by measure
  const isTimeBasedSource = vocalsEnabled || videoEnabled;
  const songSlug = hymn?.slug;
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const { measureVisits, measurePercentages, measurePercentagesWithRepeats } = use(CurrentSongContext);
  const [locationSliderUpdateTimestamp, setLocationSliderUpdateTimestamp] = useState(0);
  const [pointerDown, setPointerDown] = React.useState(false);
  const [experiencingUserInputOnTimeSlider, setExperiencingUserInputOnTimeSlider] = React.useState(false);
  const [locationValue, setLocationValue] = useState('');
  const locationSliderRef = useRef<HTMLInputElement>(null);
  const { addFavorite, removeFavorite, includesHymn } = use(FavoritesContext);
  const [voiceValues, setVoiceValues] = useLocalStorage(LocalStorageKey.VoiceValues);
  const isFavorite = useMemo(() => !!hymn && includesHymn(hymn), [hymn, includesHymn]);
  const [selectedVoice, setSelectedVoice] = useState<Voice | undefined>();
  const isLoaded = sypFileParsed && readyToPlay;
  const [songIntroduction] = useLocalStorage(LocalStorageKey.SongIntroduction);
  const [repeatMode, setRepeatMode] = useLocalStorage(LocalStorageKey.Repeat);

  // note that CC 2002 contains some single measure, hand-typed music - one measure isn't useful anyway
  const isMeasureBasedUI = !isTimeBasedSource && measurePercentages && measurePercentages.length > 1;

  const updateLocationValue = useCallback(
    (percent: number) => {
      if (duration === undefined || !readyToPlay) {
        return;
      }
      const { measureIndex } = getMeasureFromPercent(percent, measurePercentages);
      if (isMeasureBasedUI && measureIndex !== undefined && sypFile) {
        const measureNumber = sypFile.measures[measureIndex].number;
        setLocationValue(getMeasureNumberForDisplay(measureNumber));
        return;
      }
      setLocationValue(formatMinutesSeconds(duration * (percent / 100)));
    },
    [duration, readyToPlay, measurePercentages, isMeasureBasedUI, sypFile],
  );

  const addHymnAsFavorite = useCallback(() => {
    if (!hymn) {
      return;
    }
    addFavorite(hymn);
  }, [addFavorite, hymn]);

  const removeHymnAsFavorite = useCallback(() => {
    if (!hymn) {
      return;
    }
    removeFavorite(hymn);
  }, [hymn, removeFavorite]);

  const renderFavoriteButton = useCallback(() => {
    if (!hymn || isCustomSong(hymn.slug)) {
      // custom songs do not have a hymnal, so cannot yet be marked as favorite
      // TODO(hewitt): Convert favorites table to JSON blob in order to store custom song info
      //               Best to wait until we nix Coda and have a better idea of how we will store custom music
      return null;
    }
    if (isFavorite) {
      return <HeartIconWrapper onClick={removeHymnAsFavorite} />;
    } else {
      return <OutlinedHeartIconWrapper onClick={addHymnAsFavorite}>Add Favorite</OutlinedHeartIconWrapper>;
    }
  }, [hymn, isFavorite, removeHymnAsFavorite, addHymnAsFavorite]);

  const [rawVerseCount] = useLocalStorage(LocalStorageKey.VerseCount);
  const versesInRoutedSong = useMemo(() => {
    if (!measureVisits || measureVisits.length === 0 || !songContainsRepeats) {
      return;
    }
    return getVerseCount(measureVisits);
  }, [measureVisits, songContainsRepeats]);
  const verseCount = versesInRoutedSong ?? rawVerseCount ?? hymn?.verseCount ?? defaultVerseCount;

  const handleVoiceSliderChange = useCallback(
    (voice: Voice, value: number) => {
      setSelectedVoice(undefined);
      setVoiceValues(fixVoiceValues({ ...voiceValues, [voice]: value }));
    },
    [setVoiceValues, voiceValues],
  );

  // percentage of the specified measure's duration
  const getMeasureDurationPercentage = useCallback((index: number, percentages: number[]) => {
    const startOfMeasurePercentage = percentages[index];
    const endOfMeasurePercentage = index + 1 < percentages.length ? percentages[index + 1] : 100;
    return endOfMeasurePercentage - startOfMeasurePercentage;
  }, []);

  const getSliderPercentFromSongPercent = useCallback(
    (songPercent: number | undefined): { percent?: number; pass?: VisitPass } => {
      if (
        songPercent === undefined ||
        measureVisits === undefined ||
        !songContainsRepeats ||
        !measurePercentages ||
        !measurePercentagesWithRepeats
      ) {
        return { percent: songPercent };
      }
      const { measureIndex } = getMeasureFromPercent(songPercent, measurePercentagesWithRepeats);
      if (measureIndex === undefined || !sypFile) {
        return { percent: songPercent };
      }

      // map percentage of total song to slider by adjusting how far we are in the current measure
      // note that measure percentages are smaller in the total song (with repeats) than in the slider (no repeats)
      const { measure, verse, iteration } = measureVisits[measureIndex];
      const measureDurationPercentage = getMeasureDurationPercentage(measureIndex, measurePercentagesWithRepeats);
      const percentageOfCurrentMeasure =
        (songPercent - measurePercentagesWithRepeats[measureIndex]) / measureDurationPercentage;
      const sliderMeasureIndex = sypFile?.measures.indexOf(measure);
      const sliderMeasureDurationPercentage = getMeasureDurationPercentage(sliderMeasureIndex, measurePercentages);

      return {
        percent: measurePercentages[sliderMeasureIndex] + percentageOfCurrentMeasure * sliderMeasureDurationPercentage,
        pass: { verse, iteration },
      };
    },
    [
      getMeasureDurationPercentage,
      measureVisits,
      measurePercentages,
      songContainsRepeats,
      measurePercentagesWithRepeats,
      sypFile,
    ],
  );

  // the visit of the slider's measure a seek lands on, keeping the given pass; undefined when the slider maps
  // straight to the song (a recording, a song without repeats)
  const getVisitIndexFromSliderPercent = useCallback(
    (sliderPercent: number, pass: VisitPass): number | undefined => {
      if (isTimeBasedSource || !songContainsRepeats || !measurePercentages || !measurePercentagesWithRepeats) {
        return;
      }
      const { measureIndex } = getMeasureFromPercent(sliderPercent, measurePercentages);
      if (measureIndex === undefined || !sypFile) {
        return;
      }
      return findVisitIndexToSeek(measureVisits, sypFile.measures[measureIndex].number, pass);
    },
    [measureVisits, measurePercentages, songContainsRepeats, measurePercentagesWithRepeats, sypFile, isTimeBasedSource],
  );

  // the verse number (1 based) and pass of the measure visit under the playback position, kept as two numbers so
  // that the slider's interval tick re-renders only when they change
  const [currentVerseNumber, setCurrentVerseNumber] = useState<number | undefined>();
  const [currentIteration, setCurrentIteration] = useState<number | undefined>();
  const setCurrentPass = useCallback((pass: VisitPass | undefined) => {
    setCurrentVerseNumber(pass?.verse);
    setCurrentIteration(pass?.iteration);
  }, []);
  // While on the introduction (raw verse 0), the position-derived verse would point at the
  // final verse, where the introduction seeks to — show the Int verse instead
  const isOnIntroduction = songIntroduction !== undefined && currentVerseRaw === 0;
  const currentVerse =
    songContainsRepeats && currentVerseNumber !== undefined && !isOnIntroduction
      ? verseIndexFromNumber(currentVerseNumber, !!songIntroduction)
      : currentVerseRaw;

  const updateLocationSlider = useCallback(
    (percentOverride?: number) => {
      // debounce slider update immediately after the location slider has been changed to avoid bouncing back to
      // the previous location
      const diff = Date.now() - locationSliderUpdateTimestamp;
      if (percentOverride === undefined && diff < 50) {
        return;
      }
      const { percent: sliderPercent, pass } = getSliderPercentFromSongPercent(getCurrentPercent());
      const rawPercent = percentOverride ?? sliderPercent;
      if (
        (percentOverride === undefined && experiencingUserInputOnTimeSlider) ||
        !locationSliderRef.current ||
        rawPercent === undefined
      ) {
        return;
      }
      const percent = roundMeasurePercent(rawPercent);
      locationSliderRef.current.value = percent.toString();
      updateLocationValue(percent);
      setCurrentPass(pass);
    },
    [
      experiencingUserInputOnTimeSlider,
      getCurrentPercent,
      getSliderPercentFromSongPercent,
      locationSliderUpdateTimestamp,
      setCurrentPass,
      updateLocationValue,
    ],
  );

  useEffect(() => {
    if (!isPlayerMaximized) {
      return;
    }
    const interval = window.setInterval(updateLocationSlider, locationSliderUpdateIntervalMs);
    return () => window.clearInterval(interval);
  }, [isPlayerMaximized, updateLocationSlider]);

  const getChunkyPercentFromSliderValue = useCallback(
    (percent: number): number => {
      if (isTimeBasedSource || !measurePercentages) {
        return percent;
      }
      const { measurePercent } = getMeasureFromPercent(percent, measurePercentages);
      if (measurePercent !== undefined) {
        return measurePercent;
      }
      return Number(percent);
    },
    [measurePercentages, isTimeBasedSource],
  );

  // The one seek of the slider and the verse button, in a fixed order: the thumb, the verse under it (after
  // updateLocationSlider, which sets the pass under the old position), the sequencer, then the timestamp that keeps
  // the next interval tick from reading the position before the seek lands. The thumb moves at once because the
  // interval tick only runs while the player is maximised.
  const seek = useCallback(
    ({ songPercent, sliderPercent, pass }: { songPercent: number; sliderPercent: number; pass?: VisitPass }) => {
      updateLocationSlider(sliderPercent);
      if (pass) {
        setCurrentPass(pass);
      }
      sequencer.setNewTime(songPercent);
      setLocationSliderUpdateTimestamp(Date.now());
    },
    [sequencer, setCurrentPass, updateLocationSlider],
  );

  // a seek to the start of one visit of a song with repeats
  const seekToVisit = useCallback(
    (visitIndex: number) => {
      const visit = measureVisits[visitIndex] as MeasureVisit | undefined;
      const songPercent = measurePercentagesWithRepeats?.[visitIndex];
      const sliderMeasureIndex = visit && sypFile ? sypFile.measures.indexOf(visit.measure) : -1;
      const sliderPercent = sliderMeasureIndex === -1 ? undefined : measurePercentages?.[sliderMeasureIndex];
      if (!visit || songPercent === undefined || sliderPercent === undefined) {
        return;
      }
      seek({ songPercent, sliderPercent, pass: { verse: visit.verse, iteration: visit.iteration } });
    },
    [measurePercentages, measurePercentagesWithRepeats, measureVisits, seek, sypFile],
  );

  const setSequencerTime = useCallback(
    (chunkySliderPercent: number) => {
      // keep the pass under the playback position; on the introduction, which has none, aim for verse 1
      const pass: VisitPass =
        currentVerseNumber !== undefined && !isOnIntroduction
          ? { verse: currentVerseNumber, iteration: currentIteration }
          : { verse: Math.max(1, verseNumberFromIndex(currentVerse, !!songIntroduction)) };
      const visitIndex = getVisitIndexFromSliderPercent(chunkySliderPercent, pass);
      if (visitIndex === undefined) {
        seek({ songPercent: chunkySliderPercent, sliderPercent: chunkySliderPercent });
        return;
      }
      seekToVisit(visitIndex);
    },
    [
      currentIteration,
      currentVerse,
      currentVerseNumber,
      getVisitIndexFromSliderPercent,
      isOnIntroduction,
      seek,
      seekToVisit,
      songIntroduction,
    ],
  );

  const onVerseClick = useCallback(
    (verseIndex: number) => {
      if (!songContainsRepeats || (songIntroduction && verseIndex === 0)) {
        sequencer.changeVerse(verseIndex);
        return;
      }
      // a song with repeats plays all its verses as one pass through the music: seek to the verse's first visit,
      // as a drag of the location slider does (a practice video's timeline is not the music's, so it stays put)
      if (isTimeBasedSource) {
        return;
      }
      const visitIndex = getVerseStartVisitIndex(measureVisits, verseNumberFromIndex(verseIndex, !!songIntroduction));
      if (visitIndex !== undefined) {
        seekToVisit(visitIndex);
      }
    },
    [isTimeBasedSource, measureVisits, seekToVisit, sequencer, songContainsRepeats, songIntroduction],
  );

  const cycleRepeatMode = useCallback(() => {
    const nextMode = {
      [RepeatMode.Off]: RepeatMode.SongList,
      [RepeatMode.SongList]: RepeatMode.Song,
      [RepeatMode.Song]: RepeatMode.Off,
    };
    setRepeatMode(nextMode[repeatMode]);
  }, [repeatMode, setRepeatMode]);

  const handleSwipe = useCallback(() => {
    showPDF();
  }, [showPDF]);

  const swipeLeftHandler = useSwipeable({
    delta: swipeDeltas,
    onSwipedLeft: handleSwipe,
  });

  const onLocationSliderInput = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      const chunkyPercent = getChunkyPercentFromSliderValue(Number(event.currentTarget.value));
      updateLocationSlider(chunkyPercent);

      // Normally we don't set the slider time until the pointer up event.
      // However, iOS sends the onInput *after* onPointerUp when the user taps
      // the slider instead of drag/drop (bad Apple).
      // So, if the pointer is already up, we let onInput set the slider time.
      if (!pointerDown) {
        setSequencerTime(chunkyPercent);
      }
    },
    [getChunkyPercentFromSliderValue, updateLocationSlider, pointerDown, setSequencerTime],
  );

  const onLocationSliderPointerUp = useCallback(
    (event: PointerEvent<HTMLInputElement>) => {
      const percent = Number(event.currentTarget.value);
      if (!Number.isFinite(percent)) {
        return;
      }
      setSequencerTime(getChunkyPercentFromSliderValue(percent));
      setPointerDown(false);
      setExperiencingUserInputOnTimeSlider(false);
    },
    [getChunkyPercentFromSliderValue, setSequencerTime],
  );

  const onLocationSliderPointerDown = useCallback(() => {
    setPointerDown(true);
    setExperiencingUserInputOnTimeSlider(true);
  }, []);

  const locationValueStyle = isMeasureBasedUI
    ? { width: `${Number(locationValue.match(/^\d+/)) >= 10 ? 2 : 1}ch` }
    : {};

  const measureTickMarksGradient = useMemo(() => {
    if (isTimeBasedSource || !isMeasureBasedUI || !measurePercentages) {
      return '';
    }
    const background = 'var(--color-text)';
    const tickMark = 'var(--color-background)';
    const gradientParts: string[] = [];
    gradientParts.push(background);

    function pushTickMark(percent: number) {
      gradientParts.push(`${background} ${percent}%`);
      gradientParts.push(`${tickMark} ${percent}%`);
      gradientParts.push(`${tickMark} ${percent + 0.5}%`);
      gradientParts.push(`${background} ${percent + 0.5}%`);
    }

    for (const percent of measurePercentages) {
      if (percent > 0 && percent < 100) {
        pushTickMark(percent);
      }
    }
    return gradientParts.join(',');
  }, [measurePercentages, isMeasureBasedUI, isTimeBasedSource]);

  const locationThumbVisible = isLoaded && locationValue !== undefined;

  const locationSlider = (
    <LocationSlider
      $hiddenThumb={!locationThumbVisible}
      type="range"
      aria-label="Song location"
      ref={locationSliderRef}
      onInput={onLocationSliderInput}
      onPointerUp={onLocationSliderPointerUp}
      onPointerDown={onLocationSliderPointerDown}
      defaultValue={0}
      min={0}
      max={100}
      step={0.01}
      $gradient={measureTickMarksGradient}
    />
  );

  if (isVideoMode) {
    // the practice video's own layout: no part sliders, tempo, verses, repeat or heart (GitHub issue #4041)
    return (
      <PracticeVideoMode
        timeline={
          <VideoTimeline>
            <VideoTimeLabel>{readyToPlay ? locationValue || '0:00' : '-:--'}</VideoTimeLabel>
            {locationSlider}
            <VideoDurationLabel>{readyToPlay && duration ? formatMinutesSeconds(duration) : ''}</VideoDurationLabel>
          </VideoTimeline>
        }
      />
    );
  }

  return (
    <MixerControlsWrapper>
      <MixerSectionStack>
        <MixerSection>
          <SliderStack>
            {voices.map((voice) => {
              return (
                <SliderButtonAndRangeWrapper key={voice}>
                  <SliderButtonWrapper>
                    <VoiceButton
                      isSelected={voice === selectedVoice}
                      sequencer={sequencer}
                      setSelectedVoice={setSelectedVoice}
                      setVoiceValues={setVoiceValues}
                      voice={voice}
                      voices={voices}
                    />
                  </SliderButtonWrapper>
                  <VoiceSlider
                    voice={voice}
                    voiceValues={voiceValues}
                    handleVoiceSliderChange={handleVoiceSliderChange}
                    onLiveChange={liveControls ? setLiveVoiceValue : undefined}
                  />
                </SliderButtonAndRangeWrapper>
              );
            })}
          </SliderStack>
          {/* the song's sound: Piano / Vocals, and Video for a song with practice videos */}
          <SourceSelector />
        </MixerSection>
        <MixerSection>
          <LowerSliderStack>
            {vocalsEnabled ? null : <TempoSlider songSlug={songSlug} />}
            <LocationSliderButtonAndRangeWrapper>
              <HeartContainer>
                <div>{renderFavoriteButton()}</div>
              </HeartContainer>
              <SliderButtonWrapper>
                <SongLocation>
                  <MeasurePrefix hidden={!isMeasureBasedUI}>m.</MeasurePrefix>
                  <SliderLabel style={locationValueStyle}>{readyToPlay ? locationValue || '0' : '-'}</SliderLabel>
                </SongLocation>
              </SliderButtonWrapper>
              {locationSlider}
            </LocationSliderButtonAndRangeWrapper>
          </LowerSliderStack>
          <BottomWrapper {...swipeLeftHandler}>
            <BottomInnerWrapper>
              <BottomInnermostWrapper>
                <LowerPlayerIcons>
                  <VerseBarRow>
                    <RepeatButtonSpacer />
                    {/* a vocal recording contains all verses in one track, so verse navigation does not apply */}
                    <VerseBarWrapper hidden={vocalsEnabled}>
                      <VerseBar
                        intro={songIntroduction !== undefined}
                        verseCount={verseCount}
                        selectedVerse={currentVerse}
                        onClick={onVerseClick}
                      />
                    </VerseBarWrapper>
                    <RepeatButtonWrapper onClick={cycleRepeatMode}>
                      {repeatMode === RepeatMode.Song ? (
                        <Repeat1IconStyled />
                      ) : (
                        <RepeatIconStyled $active={repeatMode === RepeatMode.SongList} />
                      )}
                    </RepeatButtonWrapper>
                  </VerseBarRow>
                </LowerPlayerIcons>
              </BottomInnermostWrapper>
            </BottomInnerWrapper>
          </BottomWrapper>
        </MixerSection>
      </MixerSectionStack>
    </MixerControlsWrapper>
  );
}

const MixerControlsWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

const MixerSectionStack = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;

  @container ${mixerLandscapeContainerQuery} {
    flex-direction: row;
    align-items: start;
    gap: 2%;
  }
`;
const MixerSection = styled.div`
  display: flex;
  flex: 0 0 49%; // each stack gets half (less the 5% gap)
  min-width: 0;
  overflow: clip;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
`;
const SliderStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  @container ${mixerLandscapeContainerQuery} {
    margin-top: 1%;
  }
`;
const LowerSliderStack = styled(SliderStack)`
  margin-top: 0;
`;

function fixVoiceValues(voiceValues: VoiceValues): VoiceValues {
  // TODO(hewitt): Customers have reported the crash `TypeError: The provided value is non-finite`
  //               when adjusting vocal volumes.  Thus, it appears that infinite volume values are
  //               being recorded -> we attempt to prevent this here.
  return Object.fromEntries(
    Object.entries(voiceValues).map(([key, value]) => [key, Math.min(1, value)]),
  ) as unknown as VoiceValues;
}

// a whole number of seconds as m:ss
const VideoTimeline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  margin-top: 4px;
`;

const VideoTimeLabel = styled(SliderLabel)`
  flex-shrink: 0;
  min-width: 4ch;
  font-size: 15px;
`;

const VideoDurationLabel = styled.div`
  flex-shrink: 0;
  min-width: 4ch;
  text-align: right;
  font-size: 13px;
  color: var(--color-text-label);
`;

const SongLocation = styled.div`
  width: 100%;
  display: flex;
  justify-content: right;
`;
const MeasurePrefix = styled(SliderLabel)`
  padding-right: 1px;
`;

const LocationSliderButtonAndRangeWrapper = styled(SliderButtonAndRangeWrapper)`
  position: relative;
  margin: 10px 0;
`;
const LocationSlider = styled.input<{ $gradient: string; $hiddenThumb: boolean }>`
  -webkit-appearance: none;
  width: 100%;
  /* Should be shared with SliderBase, but styled component inheritance fails in this case - keep these in sync! */

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 8.4px;
    cursor: pointer;
    background: var(--color-slider);
  }

  &::-webkit-slider-runnable-track {
    background: linear-gradient(to right, ${(props) => props.$gradient});
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    box-shadow: none;
    border: 4px solid var(--color-slider-thumb-border);
    height: 25px;
    width: 25px;
    margin-top: -8.5px;
    margin-left: -0.5px;
    border-radius: 50%;
    background: var(--color-background);
    position: relative;

    ${(props) =>
      props.$hiddenThumb &&
      `
      -webkit-appearance: none;
      background: transparent;
      border: none;
    `}
  }
`;
const heartIconStyles = css`
  width: 33px;
  cursor: pointer;
  fill: var(--color-text);
`;

const HeartIconWrapper = styled(HeartIcon)`
  ${heartIconStyles}
  fill: var(--color-favorite-heart);
`;

const OutlinedHeartIconWrapper = styled(OutlinedHeartIcon)`
  ${heartIconStyles}
`;

const BottomWrapper = styled.div`
  display: block;
  width: 100%;

  @container ${mixerLandscapeContainerQuery} {
    margin-top: 8px;
  }
`;

const BottomInnerWrapper = styled.div`
  position: relative;
  @container ${mixerLandscapeContainerQuery} {
    margin-top: -20px;
  }
`;

const BottomInnermostWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const LowerPlayerIcons = styled.div`
  margin-top: 20px;
  width: 100%;
  text-align: center;
`;

const VerseBarRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  width: 100%;
  padding: 0 10px;
`;

const RepeatButtonSpacer = styled.div``;

const RepeatButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const repeatIconStyles = css`
  width: 28px;
  height: 28px;
`;

const RepeatIconStyled = styled(RepeatIcon)<{ $active: boolean }>`
  ${repeatIconStyles}
  color: ${(props) => (props.$active ? 'var(--color-nightingale-light)' : 'var(--color-text)')};
`;

const Repeat1IconStyled = styled(Repeat1Icon)`
  ${repeatIconStyles}
  color: var(--color-nightingale-light);
`;

const VerseBarWrapper = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const HeartContainer = styled.div`
  position: absolute;
  left: 10px;
  width: 33px;
`;
